import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';

import { CmsSiteService } from '../../models/content';
import { ModalService } from '../../services/modal.service';

/**
 * Website service record editor (SERVICES-CMS.md §3). Structured fields only
 * — the page's wording lives in the Pages editor. The id is the join key
 * every derived surface hangs off ({price:<id>} tokens, the page's
 * service-* blocks, the home cards), so it is typed once on create and
 * locked forever after.
 */
@Component({
  selector: 'app-content-site-service',
  templateUrl: './content-site-service.component.html',
})
export class ContentSiteServiceComponent implements OnInit {
  service!: CmsSiteService;
  isNew = false;
  /** True while the save request is in flight — Save spins, Cancel locks. */
  saving = false;
  form!: FormGroup;
  existingIds: string[] = [];

  inclusions: Array<{ text: string }> = [];
  faqs: Array<{ question: string; answer: string }> = [];

  categoryOptions = [
    { label: 'Cremation', value: 'cremation' },
    { label: 'Burial', value: 'burial' },
    { label: 'Memorial', value: 'memorial' },
    { label: 'Pre-paid', value: 'prepaid' },
    { label: 'Livestream', value: 'livestream' },
  ];

  constructor(
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    private fb: FormBuilder,
    private modalService: ModalService
  ) {}

  ngOnInit(): void {
    this.service = this.config.data?.service;
    this.isNew = this.config.data?.isNew === true;
    this.existingIds = this.config.data?.existingIds ?? [];

    this.form = this.fb.group({
      id: [
        { value: this.service._meta.id || '', disabled: !this.isNew },
        [Validators.required, Validators.pattern(/^[a-z0-9][a-z0-9-]*$/)],
      ],
      name: [this.service.name || '', Validators.required],
      category: [this.service.category || 'cremation', Validators.required],
      shortDescription: [this.service.shortDescription || ''],
      fullDescription: [this.service.fullDescription || ''],
      startingPrice: [this.service.startingPrice ?? null],
      order: [this.service.order ?? 99],
      featured: [this.service.featured === true],
      published: [this.service.published !== false],
    });

    const rawInclusions: any = this.service.inclusions;
    this.inclusions = (Array.isArray(rawInclusions) ? rawInclusions : Object.values(rawInclusions || {}))
      .filter((x: any) => typeof x === 'string')
      .map((text: string) => ({ text }));
    const rawFaqs: any = this.service.faqs;
    this.faqs = (Array.isArray(rawFaqs) ? rawFaqs : Object.values(rawFaqs || {}))
      .filter((f: any) => f && typeof f === 'object')
      .map((f: any) => ({ question: f.question || '', answer: f.answer || '' }));
  }

  addInclusion(): void {
    this.inclusions.push({ text: '' });
  }
  removeInclusion(i: number): void {
    this.inclusions.splice(i, 1);
  }
  moveInclusion(i: number, delta: number): void {
    const j = i + delta;
    if (j < 0 || j >= this.inclusions.length) return;
    const [x] = this.inclusions.splice(i, 1);
    this.inclusions.splice(j, 0, x);
  }

  addFaq(): void {
    this.faqs.push({ question: '', answer: '' });
  }
  removeFaq(i: number): void {
    this.faqs.splice(i, 1);
  }
  moveFaq(i: number, delta: number): void {
    const j = i + delta;
    if (j < 0 || j >= this.faqs.length) return;
    const [x] = this.faqs.splice(i, 1);
    this.faqs.splice(j, 0, x);
  }

  onSave(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    // getRawValue: `id` is disabled on existing records and would otherwise
    // vanish from form.value — turning every save into a new record.
    const value = this.form.getRawValue();

    // Upsert has no create-vs-update distinction — a new record with an
    // existing id would silently replace that record.
    if (this.isNew && this.existingIds.includes(value.id)) {
      this.modalService.generic({
        title: "This service can't be saved yet",
        copy: [`A service with the id "${value.id}" already exists — saving would silently overwrite it. Pick a different id, or edit the existing record instead.`],
        buttons: [{ label: 'Close', key: 'close', class: 'p-button-text' }],
      });
      return;
    }

    const price = value.startingPrice;
    if (price !== null && price !== undefined && price !== '' && !(Number(price) > 0)) {
      this.modalService.generic({
        title: "This service can't be saved yet",
        copy: ['The starting price must be a number above zero — or left empty to show no price.'],
        buttons: [{ label: 'Close', key: 'close', class: 'p-button-text' }],
      });
      return;
    }

    const out: any = {
      _meta: { id: value.id, index: this.service._meta.index ?? 0 },
      name: value.name.trim(),
      category: value.category,
      shortDescription: (value.shortDescription || '').trim(),
      fullDescription: (value.fullDescription || '').trim(),
      order: typeof value.order === 'number' ? value.order : 99,
      featured: value.featured === true,
      published: value.published !== false,
      inclusions: this.inclusions.map((x) => x.text.trim()).filter(Boolean),
      faqs: this.faqs
        .map((f) => ({ question: f.question.trim(), answer: f.answer.trim() }))
        .filter((f) => f.question && f.answer),
    };
    if (price !== null && price !== undefined && price !== '') out.startingPrice = Number(price);

    // Save-then-close: see the sibling editors — the dialog stays open (Save
    // spinning) until the server confirms, so a publish cannot race the write.
    const save = this.config.data.save as ((v: any) => any) | undefined;
    if (!save) {
      this.ref.close({ saved: true, service: out });
      return;
    }
    this.saving = true;
    save(out).subscribe({
      next: () => this.ref.close({ saved: true, service: out, persisted: true }),
      error: (err: any) => {
        this.saving = false;
        this.modalService.error(err?.error?.error || err);
      },
    });
  }

  onCancel(): void {
    this.ref.close({ saved: false });
  }
}
